/**
 * Agent Skills store — mirrors the /api/skills REST surface.
 *
 * Provides list/install/preview/create/save/delete/toggle/update actions
 * backing both the Skills tab in Settings and the Skill editor page.
 * Keeps one flat array of skills across all scopes; consumers filter by
 * scope client-side for rendering.
 */

import { create } from "zustand";
import type {
  Skill,
  SkillsListResponse,
  SkillDetailResponse,
  SkillPreviewResponse,

  SkillValidateResponse,
} from "@/api/types";
import { apiFetch } from "@/api/client";

interface SkillsState {
  skills: Skill[];
  isLoading: boolean;
  error: string | null;

  loadSkills: () => Promise<void>;

  /** Fetch one skill with its full SKILL.md body for the editor. */
  getSkillDetail: (id: number) => Promise<SkillDetailResponse>;

  /** Dry-run preview of an install source (no writes). */
  previewSource: (source: string) => Promise<SkillPreviewResponse>;

  /** Install some or all skills from a source. */
  installFromSource: (
    source: string,
    selectedNames?: string[],
  ) => Promise<void>;

  /** Re-pull an already-installed skill from its remembered source. */
  updateFromSource: (id: number) => Promise<void>;

  /** Create a new skill from editor input. */
  createSkill: (payload: {
    name: string;
    description: string;
    body?: string;
    requires_mcp?: string[];
  }) => Promise<Skill>;

  /** Save editor changes back to disk. */
  saveSkill: (
    id: number,
    payload: {
      name: string;
      description: string;
      body: string;
      requires_mcp: string[];
    },
  ) => Promise<Skill>;

  /** Delete a user or project skill. Bundled rejects with 403. */
  deleteSkill: (id: number) => Promise<void>;

  /** Toggle enabled state without touching the file. */
  toggleSkill: (id: number, enabled: boolean) => Promise<void>;

  /** Open the skill directory in the OS file manager. */
  openFolder: (id: number) => Promise<void>;

  /** Live validation for the editor — no DB interaction. */
  validate: (payload: {
    name: string;
    description: string;
    body?: string;
    requires_mcp?: string[];
  }) => Promise<SkillValidateResponse>;
}

export const useSkillsStore = create<SkillsState>((set, get) => ({
  skills: [],
  isLoading: false,
  error: null,

  loadSkills: async () => {
    set({ isLoading: true, error: null });
    try {
      const res = await apiFetch<SkillsListResponse>("/skills");
      set({ skills: res.skills, isLoading: false });
    } catch (err) {
      set({ isLoading: false, error: (err as Error).message });
      throw err;
    }
  },

  getSkillDetail: async (id) => {
    return apiFetch<SkillDetailResponse>(`/skills/${id}`);
  },

  previewSource: async (source) => {
    return apiFetch<SkillPreviewResponse>("/skills/preview", {
      method: "POST",
      body: JSON.stringify({ source }),
    });
  },

  installFromSource: async (source, selectedNames) => {
    await apiFetch("/skills/install", {
      method: "POST",
      body: JSON.stringify({
        source,
        selected_names: selectedNames ?? null,
      }),
    });
    await get().loadSkills();
  },

  updateFromSource: async (id) => {
    await apiFetch(`/skills/${id}/update`, { method: "POST" });
    await get().loadSkills();
  },

  createSkill: async (payload) => {
    const res = await apiFetch<{ skill: Skill }>("/skills", {
      method: "POST",
      body: JSON.stringify(payload),
    });
    await get().loadSkills();
    return res.skill;
  },

  saveSkill: async (id, payload) => {
    const res = await apiFetch<{ skill: Skill }>(`/skills/${id}`, {
      method: "PUT",
      body: JSON.stringify(payload),
    });
    await get().loadSkills();
    return res.skill;
  },

  deleteSkill: async (id) => {
    await apiFetch(`/skills/${id}`, { method: "DELETE" });
    await get().loadSkills();
  },

  openFolder: async (id) => {
    await apiFetch(`/skills/${id}/open-folder`, { method: "POST" });
  },

  toggleSkill: async (id, enabled) => {
    await apiFetch(`/skills/${id}/toggle`, {
      method: "POST",
      body: JSON.stringify({ enabled }),
    });
    // Optimistic local update so the toggle feels instant; the next
    // full load will reconcile any server-side side effects.
    set((state) => ({
      skills: state.skills.map((s) =>
        s.id === id ? { ...s, enabled } : s,
      ),
    }));
  },

  validate: async (payload) => {
    return apiFetch<SkillValidateResponse>("/skills/validate", {
      method: "POST",
      body: JSON.stringify({
        name: payload.name,
        description: payload.description,
        body: payload.body ?? "",
        requires_mcp: payload.requires_mcp ?? [],
      }),
    });
  },
}));
